import {
  COURSE_DOES_NOT_EXIST_ERROR,
  EXPIRED_SUBSCRIPTION_FOR_COURSE_ERROR,
  INVALID_SUBSCRIPTION_FOR_COURSE_ERROR,
  NO_SUBSCRIPTION_FOR_COURSE_ERROR
} from "services/courseService/errorTranslator";

import {
  AllowedCallTypes,
  makeApiStatesSelectorsFactory
} from "seneca-common/utils/apiStates";

import {
  courseCrudStateConfig,
  courseDetailsCrudStateConfig,
  courseSectionsCrudStateConfig,
  sectionCrudStateConfig
} from "../const";
import { getCoursesStateSlice, getSection } from "./state";

const getCourseMeta = (state: any) => getCoursesStateSlice(state).meta;

// Course details:
const getCourseDetailsMetaSlice = (state: any) =>
  getCourseMeta(state).get("courseDetails");

const makeCourseDetailsApiStatesSelectors = makeApiStatesSelectorsFactory(
  courseDetailsCrudStateConfig
);

export const {
  hasStarted: getCourseDetailsFetchStarted,
  didSucceed: getCourseDetailsFetchSuccess,
  didError: getCourseDetailsFetchError,
  hasFinished: courseDetailsFetchFinished,
  notSucceededOrStarted: courseDetailsNeedFetching,
  getLastFetchedTime: courseDetailsLastFetched
  // @ts-ignore
} = makeCourseDetailsApiStatesSelectors(getCourseDetailsMetaSlice)[
  AllowedCallTypes.FETCH
];

// Full courses:
const getFullCoursesMetaSlice = (state: any) =>
  getCourseMeta(state).get("fullCourses");

const makeFullCourseApiStatesSelectors = makeApiStatesSelectorsFactory(
  courseCrudStateConfig
);

export const {
  hasStarted: getFullCourseFetchStarted,
  didSucceed: getFullCourseFetchSuccess,
  didError: getFullCourseFetchError,
  hasFinished: fullCourseFetchFinished,
  notSucceededOrStarted: fullCourseNeedFetching,
  getErrorMessage: getFullCourseFetchErrorMessage
  // @ts-ignore
} = makeFullCourseApiStatesSelectors(getFullCoursesMetaSlice)[
  AllowedCallTypes.FETCH
];

export const getCourseDoesNotExist = (state: any, courseId: string): boolean =>
  fullCourseFetchFinished(state, courseId) &&
  isCourseDoesNotExistError(getFullCourseFetchErrorMessage(state, courseId));

const isCourseDoesNotExistError = (errorMessgae: string) =>
  errorMessgae === COURSE_DOES_NOT_EXIST_ERROR;

const isNoSubscriptionForCourseError = (errorMessage: string) =>
  errorMessage === NO_SUBSCRIPTION_FOR_COURSE_ERROR;

const isExpiredSubscriptionForCourseError = (errorMessage: string) =>
  errorMessage === EXPIRED_SUBSCRIPTION_FOR_COURSE_ERROR;

const isInvalidSubscriptionForCourseError = (errorMessage: string) =>
  [
    NO_SUBSCRIPTION_FOR_COURSE_ERROR,
    EXPIRED_SUBSCRIPTION_FOR_COURSE_ERROR,
    INVALID_SUBSCRIPTION_FOR_COURSE_ERROR
  ].includes(errorMessage);

export const getIsInvalidSusbcriptionForCourse = (
  state: any,
  courseId: string
): boolean =>
  fullCourseFetchFinished(state, courseId) &&
  isInvalidSubscriptionForCourseError(
    getFullCourseFetchErrorMessage(state, courseId)
  );

export const hasNoSubscriptionForCourse = (state: any, courseId: string) =>
  fullCourseFetchFinished(state, courseId) &&
  isNoSubscriptionForCourseError(
    getFullCourseFetchErrorMessage(state, courseId)
  );

export const hasExpiredSubscriptionForCourse = (state: any, courseId: string) =>
  fullCourseFetchFinished(state, courseId) &&
  isExpiredSubscriptionForCourseError(
    getFullCourseFetchErrorMessage(state, courseId)
  );

// Course sections
const getCourseSectionsMetaSlice = (state: any) =>
  getCourseMeta(state).get("courseSections");

const makeCourseSectionsApiStatesSelectors = makeApiStatesSelectorsFactory(
  courseSectionsCrudStateConfig
);

export const {
  hasStarted: getCourseSectionsFetchStarted,
  didSucceed: getCourseSectionsFetchSuccess,
  didError: getCourseSectionsFetchError,
  hasFinished: courseSectionsFetchFinished,
  notSucceededOrStarted: courseSectionsNeedFetching,
  getErrorMessage: getCourseSectionsFetchErrorMessage
  // @ts-ignore
} = makeCourseSectionsApiStatesSelectors(getCourseSectionsMetaSlice)[
  AllowedCallTypes.FETCH
];

export const getCourseSectionsDoNotExist = (
  state: any,
  courseId: string
): boolean =>
  courseSectionsFetchFinished(state, courseId) &&
  isCourseDoesNotExistError(
    getCourseSectionsFetchErrorMessage(state, courseId)
  );

// Section
const getSectionMetaSlice = (state: any) => getCourseMeta(state).get("section");

const makeSectionApiStatesSelectors = makeApiStatesSelectorsFactory(
  sectionCrudStateConfig
);

export const {
  hasStarted: getSectionFetchStarted,
  didSucceed: getSectionFetchSuccess,
  didError: getSectionFetchError,
  hasFinished: sectionFetchFinished,
  notSucceededOrStarted: sectionNeedsFetching,
  getErrorMessage: getSectionFetchErrorMessage
  // @ts-ignore
} = makeSectionApiStatesSelectors(getSectionMetaSlice)[AllowedCallTypes.FETCH];

export const getSectionDoesNotExist = (
  state: any,
  sectionId: string
): boolean => !getSection(state, sectionId);

export const getIsInvalidSubscriptionForSection = (
  state: any,
  sectionId: string
): boolean =>
  sectionFetchFinished(state, sectionId) &&
  isInvalidSubscriptionForCourseError(
    getSectionFetchErrorMessage(state, sectionId)
  );

export const hasNoSubscriptionForSection = (state: any, sectionId: string) =>
  sectionFetchFinished(state, sectionId) &&
  isNoSubscriptionForCourseError(getSectionFetchErrorMessage(state, sectionId));

export const hasExpiredSubscriptionForSection = (
  state: any,
  sectionId: string
) =>
  sectionFetchFinished(state, sectionId) &&
  isExpiredSubscriptionForCourseError(
    getSectionFetchErrorMessage(state, sectionId)
  );
